import React, {
  ReactNode,
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import useSWR from 'swr';

import { usePreferredTimeRange } from '../contexts/TimeRangeContext/TimeRangeContext';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

import { AuditLogEntryType, LogQueryArgs, pullAuditLogsForPipeline } from '../../services/logs_service';
import useClientContext from '../contexts/ClientData/ClientData';

import { modelDetailTimeRangePollInterval } from '../../config';

interface PipelineLogsContextType {
  isLoading: boolean,
  hasError: boolean,
  error: string | null,
  logs: AuditLogEntryType[],
  nextPageToken: number | null,
  handleLoadNextPage: (startIndex: number, stopIndex: number) => void | Promise<void>,
}

interface Props {
  children: ReactNode | null,
  pipelineID: string,
}

const PipelineLogsContext = React.createContext<PipelineLogsContextType>({
  logs: [],
  isLoading: false,
  error: null,
  hasError: false,
  nextPageToken: null,
  handleLoadNextPage: () => {},
})

export const usePipelineLogsContext = () => useContext(PipelineLogsContext);

export const PipelineLogsProvider = ({ 
  children,
  pipelineID,
}: Props) => {
  const [isLoadingNextPage, setIsLoadingNextPage] = useState<boolean>(false);
  const [areLogsLoading, setLogsLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<AuditLogEntryType[]>([]);
  const [nextPageToken, setNextPageToken] = useState<number | null>(null);
  const [isMounted, setMounted] = useState<boolean>(true);

  const { currentWorkspaceId } = useWorkspaceContext();
  const {
    startTimestamp: startDate,
    endTimestamp: endDate,
  }: any = usePreferredTimeRange()

  const { useArrowLogFormat } = useClientContext();

  const {
    data,
    error,
  } = useSWR([pipelineID, currentWorkspaceId, startDate, endDate, useArrowLogFormat, null, 'pipeline-audit-logs'], pullAuditLogsForPipeline, {
    refreshInterval: modelDetailTimeRangePollInterval,
    loadingTimeout: 1500,
    onLoadingSlow: () => setLogsLoading(true),
    onSuccess: () => setLogsLoading(false),
    onError: () => setLogsLoading(false),
  });

  useEffect(() => () => setMounted(false), []);

  useEffect(() => {
    if (data) {
      setLogs(data.logs);
      setNextPageToken(data.nextPageToken);
    }
  }, [data])

  const handleLoadNextPage = useCallback(() => {
    if (nextPageToken === null || isLoadingNextPage || !isMounted || pipelineID === undefined) {
      return;
    }

    setIsLoadingNextPage(true);

    const queryParams: LogQueryArgs = [pipelineID, currentWorkspaceId, startDate, endDate, useArrowLogFormat, nextPageToken];
    return pullAuditLogsForPipeline(queryParams)
      .then((response: any) => {
        setIsLoadingNextPage(false);
        setNextPageToken(response.nextPageToken);
        setLogs(log => log.concat(response.logs))
      })
  }, [currentWorkspaceId, endDate, isLoadingNextPage, isMounted, nextPageToken, pipelineID, startDate, useArrowLogFormat]);
  
  const contextValue = useMemo(() => ({
    logs,
    error,
    isLoading: areLogsLoading,
    hasError: !!error,
    nextPageToken,
    handleLoadNextPage,
  }), [logs, error, areLogsLoading, nextPageToken, handleLoadNextPage])

  return (
    <PipelineLogsContext.Provider value={contextValue}>
      {children}
    </PipelineLogsContext.Provider>
  )
}

export default usePipelineLogsContext;
